/**
 * 管理员面板 - 系统设置 / 角色管理 / 用户管理 / 模型管理
 * 精简后的编排组件，子 Tab 分别独立文件。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { fmtDateTimeCST } from '../utils/datetime';
import { XMarkIcon, ShieldCheckIcon, Cog6ToothIcon, UsersIcon, CurrencyDollarIcon, KeyIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { systemConfigApi, authApi, getErrorMessage } from '../services/api';
import { UserInfo, SystemModelConfig, UserRole } from '../types';
import UserFormDialog, { UserFormData } from './UserFormDialog';
import SystemSettingsTab from './SystemSettingsTab';
import { POSTER_SLOGANS } from './ReferralPoster';
import RolesTab from './RolesTab';
import UsersTab from './UsersTab';
import ModelsTab from './ModelsTab';
import PointsTab from './PointsTab';
import RechargeMgmtTab from './RechargeMgmtTab';
import SystemLicenseTab from './SystemLicenseTab';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type AdminTab = 'settings' | 'models' | 'roles' | 'users' | 'points' | 'recharge-mgmt' | 'license' | 'prompt';

const TABS: { key: AdminTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'settings', label: '系统设置', icon: Cog6ToothIcon },
  { key: 'models', label: '模型管理', icon: Cog6ToothIcon },
  { key: 'roles', label: '角色管理', icon: ShieldCheckIcon },
  { key: 'users', label: '用户管理', icon: UsersIcon },
  { key: 'points', label: '积分管理', icon: CurrencyDollarIcon },
  { key: 'recharge-mgmt', label: '充值管理', icon: BanknotesIcon },
  { key: 'license', label: '系统授权', icon: KeyIcon },
  { key: 'prompt', label: '提词管理', icon: Cog6ToothIcon },
];

// 提词上下文分类（与 SettingsDialog 保持一致）
const PROMPT_CONTEXT_CATEGORIES = [
  {
    category: '招标文件',
    items: [
      { key: 'project_overview', label: '项目概述' },
      { key: 'tech_scoring', label: '技术评分标准' },
      { key: 'tech_requirements', label: '技术要求' },
      { key: 'biz_scoring', label: '商务评分标准' },
      { key: 'bid_requirements', label: '投标要求' },
      { key: 'commitment', label: '相关承诺函' },
      { key: 'bid_document_format', label: '投标文件格式' },
      { key: 'disqualification', label: '废标项' },
    ],
  },
  {
    category: '知识库',
    items: [
      { key: 'project_bid_info', label: '项目投标信息' },
      { key: 'company_info', label: '企业信息' },
      { key: 'knowledge_base', label: '知识库' },
    ],
  },
];
const PROMPT_CONTEXT_KEYS = PROMPT_CONTEXT_CATEGORIES.flatMap(c => c.items);

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}

function fmtDetailOnline(sec?: number) {
  const s = sec ?? 0;
  if (s < 60) return `${s} 秒`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} 分钟`;
  const h = Math.floor(m / 60);
  return `${h} 小时 ${m % 60} 分`;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('settings');

  // 大模型配置
  const [modelConfig, setModelConfig] = useState<SystemModelConfig>({
    base_url: '', api_key: '', model: 'gpt-3.5-turbo', concurrency: 2,
    mermaid_default_view: 'code', mermaid_export_format: 'svg',
    llm_temperature: 0.3, casdoor_enabled: true,
  });
  const [configSaving, setConfigSaving] = useState(false);
  const [configMsg, setConfigMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [posterSlogansText, setPosterSlogansText] = useState('');
  const [referralRatio, setReferralRatio] = useState('0.15');

  // 模型列表
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [fetchModelsMsg, setFetchModelsMsg] = useState<string | null>(null);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [includeAllModels, setIncludeAllModels] = useState(false);
  const modelSearchRef = React.useRef<HTMLDivElement>(null);
  const modelInputRef = React.useRef<HTMLInputElement>(null);

  // 用户管理
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [userRefreshKey, setUserRefreshKey] = useState(0);

  // 用户详情（查看）
  const [viewUser, setViewUser] = useState<UserInfo | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<Record<string, any> | null>(null);

  // 提词管理
  const [promptContextTech, setPromptContextTech] = useState<Set<string>>(new Set(PROMPT_CONTEXT_KEYS.map(k => k.key)));
  const [promptContextBiz, setPromptContextBiz] = useState<Set<string>>(new Set(PROMPT_CONTEXT_KEYS.map(k => k.key)));
  const [promptConfigSaving, setPromptConfigSaving] = useState(false);
  const [promptConfigMsg, setPromptConfigMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ---- 数据加载 ----

  const loadConfig = useCallback(async () => {
    try {
      const res = await systemConfigApi.getConfig();
      if (res.data.success && res.data.config) {
        // casdoor_enabled 后端返回字符串 "true"/"false"，需转为布尔值
        const config = { ...res.data.config };
        if (typeof config.casdoor_enabled === 'string') {
          config.casdoor_enabled = config.casdoor_enabled === 'true';
        }
        setModelConfig(config);
        // 解析提词上下文配置
        const parseContext = (raw: string | undefined, fallback: Set<string>): Set<string> => {
          if (!raw) return fallback;
          try {
            const arr: string[] = JSON.parse(raw);
            return new Set(arr);
          } catch { return fallback; }
        };
        const defaultSet = new Set(PROMPT_CONTEXT_KEYS.map(k => k.key));
        setPromptContextTech(parseContext(res.data.config.prompt_context_tech, defaultSet));
        setPromptContextBiz(parseContext(res.data.config.prompt_context_biz, defaultSet));
        // 解析推荐海报标语（JSON 数组字符串），解析失败或缺失时回退内置默认
        const rawSlogans = res.data.config.poster_slogans;
        try {
          if (typeof rawSlogans === 'string' && rawSlogans.trim()) {
            JSON.parse(rawSlogans); // 校验合法性
            setPosterSlogansText(rawSlogans);
          } else {
            setPosterSlogansText(JSON.stringify(POSTER_SLOGANS, null, 2));
          }
        } catch {
          setPosterSlogansText(JSON.stringify(POSTER_SLOGANS, null, 2));
        }
        // 解析邀请积分奖励比例
        const rawRatio = res.data.config.referral_reward_ratio;
        setReferralRatio(rawRatio != null ? String(rawRatio) : '0.15');
      }
    } catch { /* 静默 */ }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadConfig();
      setConfigMsg(null);
      setShowUserForm(false);
    }
  }, [isOpen, loadConfig]);

  // ---- 模型列表 ----

  const handleFetchModels = async () => {
    if (!modelConfig.api_key.trim()) {
      setFetchModelsMsg('请先填写 API Key');
      setTimeout(() => setFetchModelsMsg(null), 3000);
      return;
    }
    setFetchingModels(true);
    setFetchModelsMsg(null);
    try {
      const res = await systemConfigApi.fetchModels({
        base_url: modelConfig.base_url, api_key: modelConfig.api_key, model_hint: modelConfig.model,
        include_all: includeAllModels,
      });
      if (res.data.success) {
        setAvailableModels(res.data.models);
        setFetchModelsMsg(res.data.message);
        if (res.data.models.length > 0) setShowModelDropdown(true);
      } else {
        setFetchModelsMsg(res.data.message || '获取失败');
      }
      setTimeout(() => setFetchModelsMsg(null), 5000);
    } catch (err) {
      setFetchModelsMsg(getErrorMessage(err, '获取模型列表失败'));
      setTimeout(() => setFetchModelsMsg(null), 5000);
    } finally { setFetchingModels(false); }
  };

  const filteredModels = availableModels.filter((m) =>
    m.toLowerCase().includes(modelConfig.model.toLowerCase())
  );

  // ---- 保存配置 ----

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    setConfigMsg(null);
    try {
      await systemConfigApi.setConfig(modelConfig);
      setConfigMsg({ type: 'success', text: '大模型配置已更新' });
      setTimeout(() => setConfigMsg(null), 3000);
    } catch (err) {
      setConfigMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
    } finally { setConfigSaving(false); }
  };

  // ---- Casdoor 独立保存 ----

  const handleSaveCasdoor = async () => {
    setConfigSaving(true);
    setConfigMsg(null);
    try {
      await systemConfigApi.setConfig({ casdoor_enabled: modelConfig.casdoor_enabled });
      await loadConfig(); // 重新加载以确认最新状态
      setConfigMsg({ type: 'success', text: '统一用户认证配置已更新' });
      setTimeout(() => setConfigMsg(null), 3000);
    } catch (err) {
      setConfigMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
    } finally { setConfigSaving(false); }
  };

  // ---- 提词管理 ----

  const togglePromptContext = (section: 'tech' | 'biz', key: string) => {
    const setter = section === 'tech' ? setPromptContextTech : setPromptContextBiz;
    const current = section === 'tech' ? promptContextTech : promptContextBiz;
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    setter(next);
  };

  const handleSavePromptConfig = async () => {
    setPromptConfigSaving(true);
    setPromptConfigMsg(null);
    try {
      const techArr = JSON.stringify(Array.from(promptContextTech));
      const bizArr = JSON.stringify(Array.from(promptContextBiz));
      await systemConfigApi.setConfig({
        prompt_context_tech: techArr,
        prompt_context_biz: bizArr,
      });
      setPromptConfigMsg({ type: 'success', text: '提词上下文默认配置已更新' });
      setTimeout(() => setPromptConfigMsg(null), 3000);
    } catch (err) {
      setPromptConfigMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
    } finally { setPromptConfigSaving(false); }
  };

  // ---- 推荐海报标语 ----
  const handleSavePosterSlogans = async () => {
    setConfigSaving(true);
    setConfigMsg(null);
    try {
      const parsed = JSON.parse(posterSlogansText);
      if (!Array.isArray(parsed)) throw new Error('标语必须为 JSON 数组');
      await systemConfigApi.setConfig({ poster_slogans: JSON.stringify(parsed) });
      await loadConfig();
      setConfigMsg({ type: 'success', text: '推荐海报标语已更新' });
      setTimeout(() => setConfigMsg(null), 3000);
    } catch (err: any) {
      setConfigMsg({ type: 'error', text: err?.message || getErrorMessage(err, '保存失败') });
    } finally {
      setConfigSaving(false);
    }
  };

  // ---- 邀请积分奖励比例 ----
  const handleSaveReferralRatio = async () => {
    setConfigSaving(true);
    setConfigMsg(null);
    try {
      const val = parseFloat(referralRatio);
      if (isNaN(val) || val <= 0 || val > 1) throw new Error('比例需在 (0, 1] 之间');
      await systemConfigApi.setConfig({ referral_reward_ratio: val });
      await loadConfig();
      setConfigMsg({ type: 'success', text: '邀请积分奖励比例已更新' });
      setTimeout(() => setConfigMsg(null), 3000);
    } catch (err: any) {
      setConfigMsg({ type: 'error', text: err?.message || getErrorMessage(err, '保存失败') });
    } finally {
      setConfigSaving(false);
    }
  };

  // ---- 用户管理操作 ----

  const openCreateForm = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const openEditForm = (u: UserInfo) => {
    setEditingUser(u);
    setShowUserForm(true);
  };

  const openViewUser = async (u: UserInfo) => {
    setViewUser(u);
    setUserDetail(null);
    setViewLoading(true);
    try {
      const res = await authApi.getUser(u.id);
      setUserDetail(res.data as Record<string, any>);
    } catch { /* 静默 */ } finally { setViewLoading(false); }
  };

  const handleSaveUser = async (form: UserFormData) => {
    if (editingUser) {
      await authApi.updateUser(editingUser.id, {
        display_name: form.display_name || undefined,
        password: form.password || undefined,
        role: form.role,
      });
    } else {
      await authApi.createUser({
        username: form.username.trim(),
        password: form.password.trim(),
        display_name: form.display_name || undefined,
        role: form.role,
      });
    }
    setUserRefreshKey((k) => k + 1);
  };

  const handleToggleActive = async (u: UserInfo) => {
    try {
      await authApi.updateUser(u.id, { is_active: !u.is_active });
      setUserRefreshKey((k) => k + 1);
    } catch { /* 静默 */ }
  };

  const handleDeleteUser = async (u: UserInfo) => {
    if (!window.confirm(`确定要删除用户「${u.username}」吗？`)) return;
    try { await authApi.deleteUser(u.id); setUserRefreshKey((k) => k + 1); }
    catch (err) { alert(getErrorMessage(err, '删除失败')); }
  };

  // ---- 渲染 ----

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[5vh]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">管理面板</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs 导航 */}
        <div className="flex-shrink-0 px-6 pt-4 border-b border-gray-100">
          <nav className="flex space-x-1 -mb-px" aria-label="Tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 rounded-t-lg transition-colors ${
                    isActive ? 'border-blue-500 text-blue-600 bg-blue-50/50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab 内容 */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'settings' && (
            <SystemSettingsTab
              modelConfig={modelConfig}
              setModelConfig={setModelConfig}
              configSaving={configSaving}
              configMsg={configMsg}
              handleSaveConfig={handleSaveConfig}
              handleSaveCasdoor={handleSaveCasdoor}
              availableModels={availableModels}
              fetchingModels={fetchingModels}
              fetchModelsMsg={fetchModelsMsg}
              showModelDropdown={showModelDropdown}
              setShowModelDropdown={setShowModelDropdown}
              filteredModels={filteredModels}
              handleFetchModels={handleFetchModels}
              includeAllModels={includeAllModels}
              setIncludeAllModels={setIncludeAllModels}
              modelSearchRef={modelSearchRef}
              modelInputRef={modelInputRef}
              posterSlogansText={posterSlogansText}
              setPosterSlogansText={setPosterSlogansText}
              handleSavePosterSlogans={handleSavePosterSlogans}
              referralRatio={referralRatio}
              setReferralRatio={setReferralRatio}
              handleSaveReferralRatio={handleSaveReferralRatio}
            />
          )}
          {activeTab === 'models' && <ModelsTab />}
          {activeTab === 'roles' && <RolesTab />}
          {activeTab === 'users' && (
            <UsersTab
              refreshKey={userRefreshKey}
              openCreateForm={openCreateForm}
              openEditForm={openEditForm}
              openViewUser={openViewUser}
              handleToggleActive={handleToggleActive}
              handleDeleteUser={handleDeleteUser}
            />
          )}
          {activeTab === 'points' && <PointsTab />}
          {activeTab === 'recharge-mgmt' && <RechargeMgmtTab />}
          {activeTab === 'license' && <SystemLicenseTab />}
          {activeTab === 'prompt' && (
            <div className="space-y-6">
              <div className="text-sm text-gray-600">
                设置系统默认的提词上下文配置。控制生成技术标/商务标章节内容时，向 AI 传入哪些上下文信息。用户可在"我的设置"中覆盖这些默认值。
              </div>

              {promptConfigMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  promptConfigMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {promptConfigMsg.text}
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                {/* 技术部分 */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100">
                    技术部分默认上下文
                  </h3>
                  <div className="space-y-3">
                    {PROMPT_CONTEXT_CATEGORIES.map(cat => (
                      <div key={`tech-${cat.category}`}>
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5 ml-1">
                          {cat.category}
                        </div>
                        <div className="space-y-0.5">
                          {cat.items.map(item => (
                            <label
                              key={`tech-${item.key}`}
                              className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={promptContextTech.has(item.key)}
                                onChange={() => togglePromptContext('tech', item.key)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 select-none">{item.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 商务部分 */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100">
                    商务部分默认上下文
                  </h3>
                  <div className="space-y-3">
                    {PROMPT_CONTEXT_CATEGORIES.map(cat => (
                      <div key={`biz-${cat.category}`}>
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5 ml-1">
                          {cat.category}
                        </div>
                        <div className="space-y-0.5">
                          {cat.items.map(item => (
                            <label
                              key={`biz-${item.key}`}
                              className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={promptContextBiz.has(item.key)}
                                onChange={() => togglePromptContext('biz', item.key)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 select-none">{item.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 保存按钮 */}
              <div className="flex justify-end">
                <button
                  onClick={handleSavePromptConfig}
                  disabled={promptConfigSaving}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {promptConfigSaving ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />
                      保存中...
                    </>
                  ) : (
                    '保存默认配置'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 新增/编辑用户弹窗 */}
      <UserFormDialog
        editingUser={editingUser}
        isOpen={showUserForm}
        onClose={() => setShowUserForm(false)}
        onSave={handleSaveUser}
      />

      {/* 用户详情弹窗（查看） */}
      {viewUser && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setViewUser(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">用户详情</h3>
              <button onClick={() => setViewUser(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            {viewLoading ? (
              <div className="text-center py-8 text-sm text-gray-400">加载中...</div>
            ) : userDetail ? (
              <div className="text-sm">
                <DetailRow label="用户名" value={`${userDetail.display_name || userDetail.username}（@${userDetail.username}）`} />
                <DetailRow label="角色" value={userDetail.role} />
                <DetailRow label="项目数" value={String(userDetail.project_count)} />
                <DetailRow label="企业管理-企业数" value={String(userDetail.enterprise_count)} />
                <DetailRow label="知识库数" value={String(userDetail.kb_count)} />
                <DetailRow label="已用积分" value={String(userDetail.total_points_used)} />
                <DetailRow label="剩余积分" value={String(userDetail.points)} />
                <DetailRow label="充值累计金额" value={`¥${(userDetail.recharge_total_amount ?? 0).toFixed(2)}`} />
                <DetailRow label="累计在线时长" value={fmtDetailOnline(userDetail.total_online_seconds)} />
                <DetailRow label="最近一次登录" value={fmtDateTimeCST(userDetail.last_login_at)} />
              </div>
            ) : (
              <div className="text-center py-8 text-sm text-gray-400">无数据</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
